import React, { useEffect, useRef } from 'react';

import PDFViewer from './PDFViewer';
import * as PDFActions from './PDFActions';
import { MobilePresentPDFToolbar } from './PDFViewerToolbar';
import PresentationToolbar from '../Shared/PresentationToolbar';

import { isMobile, toggleFullscreen } from '../utilities';
import hasFullscreenModiferKeys from '../utilities/hasFullscreenModiferKeys';

const PresentPDFToolbar = React.forwardRef((props, ref) => {
  const { currentPageIndex, isFullScreen, playerRef, psPdfKitInstance, showToolbar, totalPageCount, style } = props;

  const toolbarProps = {
    isFullScreen,
    playerRef,
    psPdfKitInstance,
    showToolbar,

    // Adapt the other PDF props into the format the presentation toolbar expects
    currentSlide: currentPageIndex + 1,
    totalSlideCount: totalPageCount,

    mobile: isMobile(),

    onGoToPage: page => PDFActions.goToPage(psPdfKitInstance, page),
    onPageBackClick: () => PDFActions.previousPage(psPdfKitInstance),
    onPageNextClick: () => PDFActions.nextPage(psPdfKitInstance),

    className: 'up-present-pdf-toolbar',
    style
  };

  return <PresentationToolbar {...toolbarProps} ref={ref} />;
});

const PresentPDFViewer = props => {
  const contextRef = useRef({ isClickingLinkAnnotation: false });

  const useAnnotationPressEffect = psPdfKitInstance => {
    useEffect(() => {
      if (psPdfKitInstance) {
        const handler = event => {
          if (event.annotation instanceof PSPDFKit.Annotations.LinkAnnotation) {
            contextRef.current.isClickingLinkAnnotation = true;
          }
        };

        psPdfKitInstance.addEventListener('annotations.press', handler);

        return () => {
          psPdfKitInstance.removeEventListener('annotations.press', handler);
        };
      }
    }, [psPdfKitInstance]);
  };

  const Toolbar = isMobile() ? MobilePresentPDFToolbar : PresentPDFToolbar;

  const documentEventHandlers = {
    click: psPdfKitInstance => () => {
      if (!contextRef.current.isClickingLinkAnnotation) {
        PDFActions.nextPage(psPdfKitInstance);
      }
    },
    keydown: (psPdfKitInstance, playerRef, isFullscreen) => e => {
      switch (e.key) {
        case ' ':
        case 'ArrowDown':
        case 'ArrowRight':
        case 'Enter':
        case 'PageDown':
        case 'Spacebar':
        case 'Down':
        case 'Right':
          PDFActions.nextPage(psPdfKitInstance);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
        case 'Left':
        case 'Up':
          PDFActions.previousPage(psPdfKitInstance);
          break;
        case 'Home':
          PDFActions.goToFirstPage(psPdfKitInstance);
          break;
        case 'End':
          PDFActions.goToLastPage(psPdfKitInstance);
          break;
        case 'f':
          if (hasFullscreenModiferKeys(e)) {
            toggleFullscreen(isFullscreen, playerRef);
          }
          break;
        case 'p':
          if (e.metaKey || e.ctrlKey) {
            psPdfKitInstance.print();
            e.preventDefault();
          }
          break;
      }
    },
    'pointerdown.capture': () => () => {
      contextRef.current.isClickingLinkAnnotation = false;
    }
  };

  return (
    <PDFViewer
      noScroll
      documentEventHandlers={documentEventHandlers}
      Toolbar={Toolbar}
      useAnnotationPressEffect={useAnnotationPressEffect}
      {...props}
    />
  );
};

export default PresentPDFViewer;
